import { UPDATE_PERSON_MESSAGE_EMAIL_STATUS, UPDATE_PERSON_MESSAGE_SUBMITTED } from "../../../../middleware/messageSignalRMiddleware";
import {
  FETCH_AVAILABILITY,
  UPDATE_ALL_AVAILABILITY,
  UPDATE_PERSON_AVAILABILITY,
  CLEAR_UPDATED_PEOPLE
} from "./actions";

interface SchedulingState {
  loading: boolean;
  saving: boolean;
  readOnly: boolean;
  refetch: boolean;
  potentialDates: any[];
  actorsAvailability: any[];
  backgroundActorsAvailability: any[];
  crewMemberAvailability: any[];
  locationsAvailability: any[];
  updatedPeople: Set<string>;
}

const defaultState: SchedulingState = {
  loading: true,
  saving: false,
  readOnly: false,
  refetch: false,
  potentialDates: [],
  actorsAvailability: [],
  backgroundActorsAvailability: [],
  crewMemberAvailability: [],
  locationsAvailability: [],
  updatedPeople: new Set(),
};

const calculateTotals = (potentialDates, actorsAvailability, backgroundActorsAvailability, crewMemberAvailability, locationsAvailability) => {
  let datesTotals: number[] = [];
  potentialDates.forEach((potentialDate) => {
    let dateCount = personCount(actorsAvailability, potentialDate.date);
    dateCount += personCount(backgroundActorsAvailability, potentialDate.date);
    dateCount += personCount(crewMemberAvailability, potentialDate.date);
    dateCount += personCount(locationsAvailability, potentialDate.date);
    datesTotals.push(dateCount);
  });

  return datesTotals;
};

const personCount = (people: any[], date) => {
  let dateCount = 0;
  people.forEach((person) => {
    if (person.availableDates.includes(date)) {
      dateCount++;
    }
  });

  return dateCount;
};

const reducer = (state = defaultState, action: any = {}) => {
  switch (action.type) {

    case FETCH_AVAILABILITY + "_PENDING": {
      const refetch = action.meta.refetch;
      return {
        ...state,
        loading: !refetch,
        refetch: false,
        errors: null
      };
    }

    case FETCH_AVAILABILITY + "_FULFILLED": {
      const potentialDates = action.payload.data.potentialDates;
      const actorsAvailability = action.payload.data.actorsAvailability;
      const backgroundActorsAvailability = action.payload.data.backgroundActorsAvailability;
      const crewMemberAvailability = action.payload.data.crewMemberAvailability;
      const locationsAvailability = action.payload.data.locationsAvailability;
      const datesTotals = calculateTotals(potentialDates, actorsAvailability, backgroundActorsAvailability, crewMemberAvailability, locationsAvailability);

      return {
        ...state,
        loading: false,
        refetch: false,
        potentialDates,
        datesTotals,
        actorsAvailability,
        backgroundActorsAvailability,
        crewMemberAvailability,
        locationsAvailability,
        canAccessActors: action.payload.data.canAccessActors,
        canAccessBackgroundActors: action.payload.data.canAccessBackgroundActors,
        canAccessCrew: action.payload.data.canAccessCrew
      };
    }

    case FETCH_AVAILABILITY + "_REJECTED": {
      return {
        ...state,
        loading: false,
        refetch: false,
        errors: action.payload.response.data.errors
      };
    }

    case UPDATE_ALL_AVAILABILITY + "_PENDING": {
      return {
        ...state,
        refetch: false,
        errors: null
      };
    }

    case UPDATE_ALL_AVAILABILITY + "_FULFILLED": {
      // Update hasSubmitted for all updated people
      const updatedActorsAvailability = state.actorsAvailability.map((actor) => {
        if (state.updatedPeople.has(`actor-${actor.id}`)) {
          return { ...actor, hasSubmitted: true };
        }
        return actor;
      });

      const updatedBackgroundActorsAvailability = state.backgroundActorsAvailability.map((backgroundActor) => {
        if (state.updatedPeople.has(`background-actor-${backgroundActor.id}`)) {
          return { ...backgroundActor, hasSubmitted: true };
        }
        return backgroundActor;
      });

      const updatedCrewMemberAvailability = state.crewMemberAvailability.map((crewMember) => {
        if (state.updatedPeople.has(`crew-${crewMember.id}`)) {
          return { ...crewMember, hasSubmitted: true };
        }
        return crewMember;
      });

      const updatedLocationsAvailability = state.locationsAvailability.map((location) => {
        if (state.updatedPeople.has(`location-${location.id}`)) {
          return { ...location, hasSubmitted: true };
        }
        return location;
      });

      return {
        ...state,
        loading: false,
        refetch: false,
        actorsAvailability: updatedActorsAvailability,
        backgroundActorsAvailability: updatedBackgroundActorsAvailability,
        crewMemberAvailability: updatedCrewMemberAvailability,
        locationsAvailability: updatedLocationsAvailability,
        updatedPeople: new Set()
      };
    }

    case UPDATE_ALL_AVAILABILITY + "_REJECTED": {
      return {
        ...state,
        loading: false,
        refetch: false,
        errors: action.payload.response.data.errors
      };
    }

    case UPDATE_PERSON_MESSAGE_SUBMITTED: {
      const { messageType, personId, personType } = action.meta;
      if (messageType !== "Availability") {
        return state;
      }

      return {
        ...state,
        refetch: true
      };
    }

    case UPDATE_PERSON_AVAILABILITY: {
      const { personType, personId, date, isAvailable } = action.meta;

      const dateString = new Date(date).toISOString().split('.')[0] + 'Z';
      const personKey = `${personType}-${personId}`;
      const updatedPeople = new Set(state.updatedPeople);
      updatedPeople.add(personKey);

      switch (personType) {
        case "background-actor": {
          const updatedBackgroundActorsAvailability = state.backgroundActorsAvailability.map((backgroundActor) => {
            if (backgroundActor.id === personId) {
              const updatedAvailableDates = isAvailable
                ? [...backgroundActor.availableDates, dateString]
                : backgroundActor.availableDates.filter((availableDate) => availableDate !== dateString);
              return {
                ...backgroundActor,
                availableDates: updatedAvailableDates
              };
            } else {
              return backgroundActor;
            }
          });
          const datesTotals = calculateTotals(state.potentialDates, state.actorsAvailability, updatedBackgroundActorsAvailability, state.crewMemberAvailability, state.locationsAvailability);
          return {
            ...state,
            datesTotals,
            backgroundActorsAvailability: updatedBackgroundActorsAvailability,
            updatedPeople
          };
        }
        case "actor": {
          const updatedActorsAvailability = state.actorsAvailability.map((actor) => {
            if (actor.id === personId) {
              const updatedAvailableDates = isAvailable
                ? [...actor.availableDates, dateString]
                : actor.availableDates.filter((availableDate) => availableDate !== dateString);
              return {
                ...actor,
                availableDates: updatedAvailableDates
              };
            } else {
              return actor;
            }
          })
          const datesTotals = calculateTotals(state.potentialDates, updatedActorsAvailability, state.backgroundActorsAvailability, state.crewMemberAvailability, state.locationsAvailability);
          return {
            ...state,
            actorsAvailability: updatedActorsAvailability,
            datesTotals,
            updatedPeople
          };
        }
        case "crew": {
          const updatedCrewMemberAvailability = state.crewMemberAvailability.map((crewMember) => {
            if (crewMember.id === personId) {
              const updatedAvailableDates = isAvailable
                ? [...crewMember.availableDates, dateString]
                : crewMember.availableDates.filter((availableDate) => availableDate !== dateString);
              return {
                ...crewMember,
                availableDates: updatedAvailableDates
              };
            } else {
              return crewMember;
            }
          });
          const datesTotals = calculateTotals(state.potentialDates, state.actorsAvailability, state.backgroundActorsAvailability, updatedCrewMemberAvailability, state.locationsAvailability);
          return {
            ...state,
            crewMemberAvailability: updatedCrewMemberAvailability,
            datesTotals,
            updatedPeople
          };
        } case "location": {
          const updatedLocationsAvailability = state.locationsAvailability.map((location) => {
            if (location.id === personId) {
              const updatedAvailableDates = isAvailable
                ? [...location.availableDates, dateString]
                : location.availableDates.filter((availableDate) => availableDate !== dateString);
              return {
                ...location,
                availableDates: updatedAvailableDates
              };
            } else {
              return location;
            }
          });

          const datesTotals = calculateTotals(state.potentialDates, state.actorsAvailability, state.backgroundActorsAvailability, state.crewMemberAvailability, updatedLocationsAvailability);
          return {
            ...state,
            locationsAvailability: updatedLocationsAvailability,
            datesTotals,
            updatedPeople
          };
        }
      }

      return state;
    }

    case CLEAR_UPDATED_PEOPLE: {
      return {
        ...state,
        updatedPeople: new Set()
      };
    }

    default:
      return state;
  }
};

export default reducer;